import React, { useState } from "react";
import { usePotentialTrades } from "../hooks/usePotentialTrades";
import { useGlobalUserMappings } from "../hooks/useGlobalHooks";
import { Info } from "lucide-react";

export default function PotentialTradesModal({ isOpen, onClose, mode, symbolStrategyBaseQty }) {
  const [threshold, setThreshold] = useState(50);
  const { potentialTrades, groupedNudges } = usePotentialTrades(threshold, mode, symbolStrategyBaseQty);
  const { userMappings } = useGlobalUserMappings();
  const safeUserMappings = Array.isArray(userMappings) ? userMappings : [];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
      <div className="bg-[#1a1d29] rounded-xl shadow-[0_0_40px_rgba(0,0,0,0.5)] border border-gray-800 w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-800 bg-[#161822]">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span className="text-blue-500">⚡</span>
              {mode === "orders" ? "Potential Entry Trades" : "Potential SL Executions"}
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <label htmlFor="threshold-input">Show orders within ₹</label>
              <input
                id="threshold-input"
                type="number"
                min="0"
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className="w-20 bg-[#0f1116] border border-gray-700 rounded px-2 py-1 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <span>of Trigger Price</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-800 rounded-md transition-colors text-gray-400 hover:text-white"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto bg-[#1a1d29] custom-scrollbar flex flex-col">
          
          {/* Nudges Summary */}
          {groupedNudges.length > 0 && (
            <div className="px-4 pt-4 pb-2 flex-shrink-0">
              <h3 className="text-gray-400 text-xs uppercase font-bold tracking-wider mb-3 px-1">Action Nudges (1x Multiplier Target)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {groupedNudges.map(nudge => (
                  <div key={nudge.id} className="bg-[#0f1116] border border-gray-700/50 rounded-lg p-3 hover:border-gray-500 transition-colors shadow-sm">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-bold text-gray-200 truncate pr-2 text-sm">{nudge.symbol}</span>
                      <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        nudge.sideLabel === "BUY" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"
                      }`}>
                        {nudge.sideLabel}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5 mb-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-500">Strategy:</span>
                        <span className="text-gray-300 font-medium truncate max-w-[120px]">{nudge.strategy}</span>
                      </div>
                      {nudge.baseQty !== "N/A" && (
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-500">1x Base Qty:</span>
                          <span className="text-blue-400 font-bold">{nudge.baseQty}</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-2 border-t border-gray-800 flex justify-between items-end">
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-500 mb-0.5">Diff to Trigger</span>
                        <span className={`font-bold text-sm leading-none ${
                            nudge.diff <= 5 ? "text-red-400 animate-pulse" :
                            nudge.diff <= 10 ? "text-orange-400" :
                            "text-yellow-400"
                          }`}>₹{nudge.diff.toFixed(2)}</span>
                      </div>
                      <div className="bg-[#1a1d29] px-2 py-1 rounded border border-gray-700 flex items-center gap-1.5 shadow-inner relative group/tooltip">
                        <span className="text-gray-400 text-[10px]">Clients:</span>
                        <span className="text-gray-200 font-bold text-sm leading-none">{nudge.clientCount}</span>
                        <Info className="w-3 h-3 text-gray-500 cursor-help ml-1 hover:text-blue-400 transition-colors" />
                        
                        <div className="absolute bottom-full right-0 mb-2 w-56 bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-2 hidden group-hover/tooltip:block z-[60]">
                          <div className="text-xs font-semibold text-gray-300 mb-1.5 border-b border-gray-700 pb-1.5 flex justify-between">
                            <span>Clients</span>
                            <span className="bg-gray-800 px-1.5 rounded text-gray-400">{nudge.clientCount}</span>
                          </div>
                          <div className="max-h-32 overflow-y-auto custom-scrollbar flex flex-col gap-1.5">
                            {nudge.clients.map(clientId => {
                              const mapping = safeUserMappings.find(u => String(u.clientId || u.client_id) === String(clientId));
                              const name = mapping ? (mapping.name || mapping.userName || mapping.UserName || mapping.Name || "Unknown") : "Unknown";
                              return (
                                <div key={clientId} className="flex justify-between items-center text-[10px] bg-[#14161f] p-1 rounded border border-gray-800/50">
                                  <span className="text-gray-500 font-mono" title={clientId}>{clientId}</span>
                                  <span className="text-gray-300 font-medium truncate ml-2 text-right" title={name}>{name}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-b border-gray-800"></div>
            </div>
          )}

          <div className="p-4 flex-1">
            {potentialTrades.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <svg
                className="w-12 h-12 mb-3 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <p className="text-lg">No potential trades right now.</p>
              <p className="text-sm mt-1">LTP is not within ₹{threshold} of any pending order triggers.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-800">
              <table className="w-full text-left text-sm text-gray-300 whitespace-nowrap">
                <thead className="text-xs text-gray-400 uppercase bg-[#0f1116] sticky top-0 z-10 shadow-sm border-b border-gray-800">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-center border-b border-gray-800">Status</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-center">Clients</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800">Symbol</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800">Strategy</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-center">Type</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-center">Side</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-right">LTP</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-right">Trigger Px</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-right">Diff</th>
                    <th className="px-4 py-3 font-semibold border-b border-gray-800 text-right">Total Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800/50">
                  {potentialTrades.map((trade, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-[#1f2231] transition-colors relative group"
                    >
                      <td className="px-4 py-3 text-center">
                        {trade.diff <= 5 ? (
                          <span className="relative flex h-3 w-3 mx-auto">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                          </span>
                        ) : trade.diff <= 10 ? (
                          <span className="relative flex h-3 w-3 mx-auto">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                          </span>
                        ) : (
                          <span className="relative flex h-3 w-3 mx-auto">
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500 opacity-80"></span>
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center">
                          <span className="font-bold text-gray-200 bg-gray-800/50 px-2 py-0.5 rounded border border-gray-700">
                            {trade.clientCount}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-gray-300">
                          {trade.symbol}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-400 text-xs">
                          {trade.strategy || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span className="text-gray-300 text-xs font-medium">
                            {trade.type}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              trade.sideLabel === "BUY"
                                ? "bg-green-500/20 text-green-400 border border-green-500/30"
                                : "bg-red-500/20 text-red-400 border border-red-500/30"
                            }`}
                          >
                            {trade.sideLabel}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-bold text-blue-400 bg-blue-900/10 px-2 py-1 rounded">
                          ₹{Number(trade.ltp).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-300 font-medium">
                          ₹{trade.comparePrice.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold px-2 py-1 rounded border ${
                          trade.diff <= 5 ? "bg-red-900/20 text-red-400 border-red-800/50" :
                          trade.diff <= 10 ? "bg-orange-900/20 text-orange-400 border-orange-800/50" :
                          "bg-yellow-900/20 text-yellow-400 border-yellow-800/50"
                        }`}>
                          ₹{trade.diff.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-gray-300 font-bold bg-[#14161f] border border-gray-700 px-2 py-1 rounded">
                          {trade.totalQty}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
  );
}
